import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../assets/css/ChatWidget.css';
import { getLocalResponse } from '../lib/siteChatLocal';

const SiteChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceReadOn, setVoiceReadOn] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [messages, setMessages] = useState([]);

  const recognitionRef = useRef(null);
  const transcriptRef = useRef('');
  const spokenTextRef = useRef('');
  const messagesEndRef = useRef(null);

  // Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.interimResults = false;
      recognitionRef.current.continuous = false;

      recognitionRef.current.onstart = () => setIsListening(true);
      recognitionRef.current.onresult = (event) => {
        transcriptRef.current = event.results[0][0].transcript;
      };
      recognitionRef.current.onend = () => {
        setIsListening(false);
        if (transcriptRef.current.trim()) {
          askQuestion(transcriptRef.current);
          transcriptRef.current = '';
        }
      };
      recognitionRef.current.onerror = () => setIsListening(false);
    }
  }, []);

  // Load session state on mount
  useEffect(() => {
    const sessionKey = 'ai-assistant-chat-state';
    const storedState = sessionStorage.getItem(sessionKey);
    
    if (storedState) {
      try {
        const { isOpen: savedIsOpen, messages: savedMessages } = JSON.parse(storedState);
        setMessages(savedMessages || []);
        setIsOpen(savedIsOpen || false);
      } catch (error) {
        console.error('Error loading chat state:', error);
      }
    }

    // Check screen size
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Persist chat state
  useEffect(() => {
    const sessionKey = 'ai-assistant-chat-state';
    sessionStorage.setItem(sessionKey, JSON.stringify({ isOpen, messages }));
  }, [isOpen, messages]);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Stop speech synthesis
  const stopSpeech = () => {
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
      spokenTextRef.current = '';
    }
  };

  // Speak response
  const speakResponse = (text) => {
    if (!voiceReadOn) return;

    const transformSpeechText = (value) => {
      const noHtml = value.replace(/<[^>]*>/g, ' ');
      const noUrls = noHtml.replace(/https?:\/\/\S+/g, ' ');
      const noParens = noUrls.replace(/\([^)]*\)/g, ' ');
      const noEmoji = noParens.replace(/([\u{1F300}-\u{1F6FF}]|[\u{1F1E6}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]|[\u{1F680}-\u{1F6FF}])/gu, ' ');
      const spacedDigits = noEmoji.replace(/\d+/g, (digits) => digits.split('').join(' '));
      return spacedDigits.replace(/\s+/g, ' ').trim();
    };

    const cleanedText = transformSpeechText(text);

    if (!cleanedText) return;

    const utterance = new SpeechSynthesisUtterance(cleanedText);
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      spokenTextRef.current = '';
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
      spokenTextRef.current = '';
    };

    speechSynthesis.speak(utterance);
    spokenTextRef.current = cleanedText;
  };

  // Close chat and stop speech
  const handleCloseChat = () => {
    stopSpeech();
    setIsOpen(false);
  };

  // Toggle voice reading
  const toggleVoiceRead = () => {
    if (isSpeaking) {
      stopSpeech();
    }
    setVoiceReadOn(!voiceReadOn);
  };

  // Start voice recognition
  const startVoiceRecognition = () => {
    if (recognitionRef.current && !isListening) {
      recognitionRef.current.start();
    }
  };

  // Ask question
  const askQuestion = async (userQuestion = question) => {
    if (!userQuestion.trim()) return;

    // Add user message
    const userMsg = {
      role: 'user',
      text: userQuestion,
    };
    setMessages((prev) => [...prev, userMsg]);
    setQuestion('');
    setLoading(true);

    try {
      // Get local response
      const localResponse = getLocalResponse(userQuestion);

      if (localResponse) {
        const botMsg = {
          role: 'bot',
          text: localResponse.answer,
          links: localResponse.links || [],
          needsClarification: localResponse.needsClarification || false,
        };
        setMessages((prev) => [...prev, botMsg]);
        speakResponse(localResponse.answer);
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMsg = {
        role: 'bot',
        text: 'Sorry, I encountered an error. Please try again later.',
        links: [],
      };
      setMessages((prev) => [...prev, errorMsg]);
    }

    setLoading(false);
  };

  const inputLocked = isListening || isSpeaking || loading;

  return (
    <div className="ai-chat-widget">
      {/* Chat Button */}
      <button
        className="chat-button"
        onClick={() => setIsOpen(!isOpen)}
        title={isOpen ? 'Close Chat' : 'Open Chat'}
      >
        💬
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          {/* Header */}
          <div className="chat-header">
            <div className="header-content">
              ⭐
              <span>AI Assistant</span>
            </div>
            <div className="header-buttons">
              <button
                className="header-btn"
                onClick={toggleVoiceRead}
                title={voiceReadOn ? 'Disable Voice Reading' : 'Enable Voice Reading'}
              >
                {voiceReadOn ? '🔊' : '🔇'}
              </button>
              <button
                className="header-btn"
                onClick={handleCloseChat}
                title="Close Chat"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Message Area */}
          <div className="message-area">
            {messages.length === 0 && (
              <div className="welcome-message">
                <p><strong>Welcome!</strong></p>
                <p>Hi! I'm here to help. Ask me about products, locations, certifications, careers, and more. 😊</p>
              </div>
            )}
            {messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.role}`}>
                <div className={`bubble ${msg.role}`}>
                  {msg.text}
                  {msg.links && msg.links.length > 0 && (
                    <div className="links-container">
                      {msg.links.map((link, linkIdx) => (
                        link.href.startsWith('http') ? (
                          <a
                            key={linkIdx}
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="chat-link"
                          >
                            👉 {link.title}
                          </a>
                        ) : (
                          <Link
                            key={linkIdx}
                            to={link.href}
                            className="chat-link"
                          >
                            👉 {link.title}
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="message bot">
                <div className="bubble bot typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Voice Control Buttons */}
          <div className="voice-controls">
            <button
              className="voice-btn"
              onClick={startVoiceRecognition}
              disabled={isListening || isSpeaking || loading}
            >
              🎤
              {isListening ? 'Listening...' : 'Start Voice Assistant'}
            </button>
            {isSpeaking && (
              <button
                className="stop-reading-btn"
                onClick={stopSpeech}
              >
                ⏹️
                Stop Reading
              </button>
            )}
          </div>

          {/* Input Area */}
          <div className="input-area">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !inputLocked && askQuestion()}
              placeholder="Ask about products, careers, quality, locations..."
              disabled={inputLocked}
              className="chat-input"
            />
            <button
              className="send-btn"
              onClick={() => askQuestion()}
              disabled={inputLocked || !question.trim()}
            >
              {loading ? '...' : isListening ? 'Mic On' : isSpeaking ? 'Reading...' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteChatWidget;
